import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

// Context for tabs state
interface TabsContextValue {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tab components must be used within a Tabs component");
  }
  return context;
};

// Context to track if we're inside tab content
interface TabContentContextValue {
  isInsideTabContent: boolean;
}

export const TabContentContext = createContext<TabContentContextValue>({
  isInsideTabContent: false,
});

export const useIsInsideTabContent = () => {
  const context = useContext(TabContentContext);
  return context.isInsideTabContent;
};

// Main Tabs component
export interface TabsProps {
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

/**
 * Arc-style Tabs component
 * 
 * Features:
 * - Animated indicator
 * - Controlled and uncontrolled modes
 * - Compound components: TabsList, Tab, TabsContent
 * - Smooth transitions
 */
const Tabs: React.FC<TabsProps> & {
  List: typeof TabsList;
  Tab: typeof Tab;
  Content: typeof TabsContent;
} = ({
  defaultValue = "",
  value,
  onChange,
  children,
  className = "",
}) => {
  const [activeTab, setActiveTabState] = useState(value || defaultValue);

  useEffect(() => {
    if (value !== undefined) {
      setActiveTabState(value);
    }
  }, [value]);

  const setActiveTab = (newValue: string) => {
    if (value === undefined) {
      setActiveTabState(newValue);
    }
    onChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

// TabsList component
export interface TabsListProps {
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "pills" | "underline";
}

const TabsList: React.FC<TabsListProps> = ({
  children,
  className = "",
  variant = "default",
}) => {
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const listRef = useRef<HTMLDivElement>(null);
  const { activeTab } = useTabsContext();

  // Update indicator position
  useEffect(() => {
    if (listRef.current) {
      const activeElement = listRef.current.querySelector(
        `[data-tab-value="${activeTab}"]`
      ) as HTMLElement;
      if (activeElement) {
        setIndicatorStyle({
          left: activeElement.offsetLeft,
          width: activeElement.offsetWidth,
        });
      }
    }
  }, [activeTab]);

  const variantStyles = {
    default: "bg-surface-secondary p-1 rounded-arc-lg",
    pills: "gap-2",
    underline: "border-b border-arc-border",
  };

  return (
    <div
      ref={listRef}
      className={`
        relative flex items-center
        ${variantStyles[variant]}
        ${className}
      `}
      role="tablist"
    >
      {children}
      
      {/* Animated indicator */}
      {variant === "default" && (
        <motion.div
          className="absolute h-[calc(100%-8px)] bg-surface-elevated rounded-arc-md shadow-arc-sm"
          initial={false}
          animate={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          style={{ top: 4 }}
        />
      )}
      
      {variant === "underline" && (
        <motion.div
          className="absolute bottom-0 h-0.5 bg-accent rounded-full"
          initial={false}
          animate={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        />
      )}
    </div>
  );
};

// Tab component
export interface TabProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

const Tab: React.FC<TabProps> = ({
  value,
  children,
  disabled = false,
  className = "",
  icon,
}) => {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-tab-value={value}
      disabled={disabled}
      onClick={() => !disabled && setActiveTab(value)}
      className={`
        relative z-10
        px-4 py-2
        text-sm font-medium
        rounded-arc-md
        transition-colors duration-arc-fast
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2
        ${isActive
          ? "text-content-primary"
          : "text-content-secondary hover:text-content-primary"
        }
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${className}
      `}
    >
      <span className="flex items-center gap-2">
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {children}
      </span>
    </button>
  );
};

// TabsContent component
export interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

const TabsContent: React.FC<TabsContentProps> = ({
  value,
  children,
  className = "",
}) => {
  const { activeTab } = useTabsContext();
  const isActive = activeTab === value;

  if (!isActive) return null;

  return (
    <TabContentContext.Provider value={{ isInsideTabContent: true }}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
        role="tabpanel"
        className={className}
      >
        {children}
      </motion.div>
    </TabContentContext.Provider>
  );
};

// Display names for debugging
Tabs.displayName = "Tabs";
TabsList.displayName = "TabsList";
Tab.displayName = "Tab";
TabsContent.displayName = "TabsContent";

// Attach compound components
Tabs.List = TabsList;
Tabs.Tab = Tab;
Tabs.Content = TabsContent;

export default Tabs;









